import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion'; // Import motion and AnimatePresence
import LogoAndLinks from './LogoAndLinks';
import './Home.css';

const images: string[] = [
  'programmer-black.png',
  'photographers-black.png',
  'dj.png'
];

const titles: string[] = [
  'Software Engineering',
  'Photography',
  'Music'
];

// Preload images
const preloadImages = (imageUrls: string[]) => {
  return Promise.all(
    imageUrls.map(url => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = url;
        img.onload = resolve;
        img.onerror = reject;
      });
    })
  );
};

const Home: React.FC = () => {
  const [index, setIndex] = useState<number>(0);
  const [showLogoAndLinks, setShowLogoAndLinks] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Preload all images before starting animations
    preloadImages(images).then(() => {
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!isLoading) {
      const timer = setInterval(() => {
        if (index < images.length - 1) {
          setIndex((prevIndex) => prevIndex + 1);
        } else {
          setShowLogoAndLinks(true);
          clearInterval(timer);
        }
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [index, images.length, isLoading]);

  const imageVariants = {
    initial: { opacity: 0, x: -200 }, // Slide from left, transparent
    animate: { opacity: 1, x: 0, transition: { duration: 1, ease: "easeOut" } }, // Fade in and slide to center
    exit: { opacity: 0, x: 200, transition: { duration: 0.5, ease: "easeIn" } }, // Slide to right and fade out
  };

  const titleVariants = {
    initial: { opacity: 0, scale: 0.5 }, // Start small and transparent
    animate: { opacity: 1, scale: 1, transition: { duration: 1, ease: "easeOut" } }, // Grow and fade in
    exit: { opacity: 0, scale: 0.5, transition: { duration: 0.5, ease: "easeIn" } }, // Shrink and fade out
  };

  if (isLoading) {
    return (
      <div className="image-autoplay-container">
      </div>
    );
  }

  return (
    <div className="image-autoplay-container">
      {!showLogoAndLinks && (
        <div className="image-and-title">
          <AnimatePresence initial={true} mode="wait">
            <motion.div
              key={index}
              variants={titleVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className='image-titles'
            >
              {titles[index]}
            </motion.div>
          </AnimatePresence>          
          <AnimatePresence initial={true} mode="wait">
            <motion.img
              key={index}
              src={images[index]}
              alt="transition"
              className="autoplay-image"
              variants={imageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            />
          </AnimatePresence>
        </div>
      )}
      {showLogoAndLinks && <LogoAndLinks />}
    </div>
  );
};

export default Home;